import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { getUserTipDefinitions, updateUserTipDefinition } from '../../api/user_tip_definitions';
import { getTags } from '../../actions/tagActions';
import Loader from '../General/Loader';
import UserTipDefinitionModal from './UserTipDefinitionModal';
import { CRITERIA_TYPES } from '../../constants/user_tip_constants';
import './UserTipDefinitions.scss';

const sortDefinitions = definitions => _.orderBy(definitions, ['id'], ['desc']);

/** Full payload for activate/deactivate updates, built from an already-parsed definition. */
const payloadFromDefinition = definition =>
  _.pick(definition, ['name', 'isActive', 'platform', 'priority', 'criteria', 'criteriaConfig', 'audience', 'tipText', 'ui']);

const activationSummary = definition =>
  definition.isActive && definition.activatedAt
    ? `Activated ${moment(definition.activatedAt).format('MMM Do YYYY')}`
    : `Drafted ${moment(definition.createdAt).format('MMM Do YYYY')}`;

const criteriaSummary = definition => {
  if (definition.criteria !== CRITERIA_TYPES.USER_CREATION_AGE) return 'Next app open';
  const { minAgeDays, maxAgeDays } = definition.criteriaConfig || {};
  return _.isNil(maxAgeDays) ? `Account age ${minAgeDays}+ days` : `Account age ${minAgeDays}–${maxAgeDays} days`;
};

const UserTipDefinitions = () => {
  const dispatch = useDispatch();
  const allTags = useSelector(state => state.tags?.tags ?? []);
  const [definitions, setDefinitions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalState, setModalState] = useState(null); // { definition: object|null, asDuplicate: boolean }

  useEffect(() => {
    getUserTipDefinitions()
      .then(defs => setDefinitions(sortDefinitions(defs)))
      .catch(err => window.ALERT.error(typeof err === 'string' ? err : 'Failed to load user tips.'))
      .finally(() => setIsLoading(false));
    dispatch(getTags()).catch(() => window.ALERT.error('Failed to load user tags.'));
  }, [dispatch]);

  const tagsById = useMemo(() => _.keyBy(allTags, 'id'), [allTags]);

  const audienceSummary = definition => {
    const { tagIds = [], userIds = [], excludeTagIds = [], match } = definition.audience || {};
    const names = tagIds.map(id => tagsById[id]?.value || `#${id}`);
    const excludedNames = excludeTagIds.map(id => tagsById[id]?.value || `#${id}`);
    const tagSummary = `${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${names.length - 3}` : ''}${tagIds.length > 1 ? ` (${match})` : ''}`;
    const exclusionSummary = `${excludedNames.slice(0, 3).join(', ')}${excludedNames.length > 3 ? ` +${excludedNames.length - 3}` : ''}`;
    const audienceUsersById = _.keyBy(definition.audienceUsers || [], 'id');
    const userNames = userIds.map(id => audienceUsersById[id]?.name || audienceUsersById[id]?.username || `#${id}`);
    const userSummary = `${userNames
      .slice(0, 3)
      .join(', ')}${userIds.length > 3 ? ` +${userIds.length - 3}` : ''}`;
    return [
      tagIds.length ? `Tags: ${tagSummary}` : null,
      userIds.length ? `Users: ${userSummary}` : null,
      excludeTagIds.length ? `Excludes: ${exclusionSummary}` : null
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const handleSaved = saved => {
    setDefinitions(prev => sortDefinitions([...prev.filter(d => d.id !== saved.id), saved]));
    setModalState(null);
  };

  const toggleActive = async definition => {
    const isActivating = !definition.isActive;
    const updated = { ...definition, isActive: isActivating, ...(isActivating ? { activatedAt: new Date().toISOString() } : {}) };
    setDefinitions(prev => prev.map(d => (d.id === definition.id ? updated : d)));
    try {
      const saved = await updateUserTipDefinition(definition.id, payloadFromDefinition(updated));
      setDefinitions(prev => sortDefinitions(prev.map(d => (d.id === definition.id ? saved : d))));
    } catch (err) {
      setDefinitions(prev => prev.map(d => (d.id === definition.id ? definition : d)));
      window.ALERT.error(typeof err === 'string' ? err : 'Failed to update the tip.');
    }
  };

  if (isLoading) {
    return (
      <div className='user-tip-definitions'>
        <Loader />
      </div>
    );
  }

  return (
    <div className='user-tip-definitions'>
      <div className='utd-header'>
        <div>
          <h1>User Tips</h1>
          <div className='utd-header-subtitle'>
            Tag- and user-targeted tips shown after code-defined onboarding tips. Configurable priority runs from 1-5, with lower values shown first.
          </div>
        </div>
        <button className='utd-btn utd-btn--primary' onClick={() => setModalState({ definition: null, asDuplicate: false })}>
          + New Tip
        </button>
      </div>

      {!definitions.length ? (
        <div className='utd-empty'>No user tips yet — create one to show a targeted banner to creators.</div>
      ) : (
        <div className='utd-list'>
          {definitions.map(definition => (
            <div key={definition.id} className={cn('utd-row', { inactive: !definition.isActive })}>
              <div className='utd-row-main'>
                <div className='utd-row-title'>
                  <span className={cn('utd-status-dot', { active: definition.isActive })} />
                  <span className='utd-row-name'>{definition.name}</span>
                  <span className='utd-row-priority'>P{definition.priority}</span>
                  <span className='utd-row-date'>{activationSummary(definition)}</span>
                </div>
                <div className='utd-row-meta'>
                  <span>{definition.platform || 'BOTH'}</span>
                  <span>{criteriaSummary(definition)}</span>
                  <span>{audienceSummary(definition)}</span>
                  {definition.ui?.button && <span>Button: {definition.ui.button.label}</span>}
                </div>
                <div className='utd-row-copy'>{definition.tipText}</div>
              </div>
              <div className='utd-row-actions'>
                <button onClick={() => setModalState({ definition, asDuplicate: false })}>Edit</button>
                <button onClick={() => setModalState({ definition, asDuplicate: true })}>Duplicate</button>
                <button className={cn('utd-toggle', { active: definition.isActive })} onClick={() => toggleActive(definition)}>
                  {definition.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalState && (
        <UserTipDefinitionModal
          definition={modalState.definition}
          asDuplicate={modalState.asDuplicate}
          allTags={allTags}
          onSave={handleSaved}
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  );
};

export default UserTipDefinitions;
